import { useMutation, useQuery } from '@tanstack/react-query';

import { endpoint } from '@shared/api/config';
import { key, STALE_TIME } from '@shared/api/query-keys';
import { httpRequest } from '@shared/api/http';
import { adoptSession, type CreateSessionPayload } from '@shared/auth/client';

import type {
  AuthConfiguration,
  CreateOtpBody,
  CreateOtpResult,
  CreateSessionBody,
  RecoverPasswordBody,
  RecoverPasswordResult,
} from './types';

/**
 * Every call the pre-session screens make, and all four go through
 * `httpRequest` rather than through `@shared/api/client`.
 *
 * That is not a shortcut. The authenticated wrapper retries a `401` behind the
 * silent-refresh mutex, and these four endpoints are `ANONYMOUS` by contract —
 * there is no session to refresh, and a stray refresh from the sign-in screen
 * would rotate a cookie that does not exist and swallow the real failure. The
 * transport module documents itself for exactly this use: `tokens:refresh`
 * reaches it the same way, and for the same reason.
 *
 * The envelope still runs. `httpRequest` unwraps `{success, message, data}` and
 * throws one `ApiError` carrying `status`, `code`, `details` and the
 * `X-Request-ID`, so the screens branch on `error_code` and never on `message`.
 */

export function authConfigurationKey() {
  return key('auth', 'authConfiguration');
}

/**
 * The sign-in surface's own parameters. `AUTH_METHODS` decides whether the
 * password block is drawn at all; the validity window seeds the resend bar; the
 * code length narrows the code field's schema.
 *
 * `staleTime` is the vocabulary default: a configuration row changes about once
 * a quarter, and re-reading it on every keystroke of a sign-in would be a
 * request per screen for a value that did not move.
 */
export function useAuthConfiguration() {
  return useQuery({
    queryKey: authConfigurationKey(),
    queryFn: () => httpRequest<AuthConfiguration>(endpoint('auth', '/authConfiguration')),
    staleTime: STALE_TIME.vocabulary,
  });
}

/**
 * Issues a code. The response carries `retry_after` and nothing else — the same
 * bytes whether the number belongs to an account or not, which is the service's
 * enumeration defence and the reason this screen can say nothing about whether
 * the number was known.
 */
export function useRequestOtp() {
  return useMutation({
    mutationFn: (body: CreateOtpBody) =>
      httpRequest<CreateOtpResult>(endpoint('auth', '/otps'), { method: 'POST', body }),
  });
}

/**
 * Signs in — or registers, which is the same act on the same screen.
 *
 * On success the payload is adopted into the in-memory session slice: it is the
 * one response on this service carrying the token **and** the identity, so no
 * `GET /api/v1/me` follows it. The refresh token never appears here; it arrives
 * as a host-only `HttpOnly` cookie this bundle cannot read.
 */
export function useCreateSession() {
  return useMutation({
    mutationFn: async (body: CreateSessionBody) => {
      const payload = await httpRequest<CreateSessionPayload>(endpoint('auth', '/sessions'), {
        method: 'POST',
        body,
      });
      adoptSession(payload);
      return payload;
    },
  });
}

/**
 * Sets the password against a fresh recovery code. This is also the settings
 * change-password implementation on the subscriber host — one endpoint, because
 * the catalog makes the two entry points one implementation.
 *
 * It revokes every live session on every host, which is why the screen sends
 * the operator back to sign in rather than dropping them into the console.
 */
export function useRecoverPassword() {
  return useMutation({
    mutationFn: (body: RecoverPasswordBody) =>
      httpRequest<RecoverPasswordResult>(endpoint('auth', '/passwords:recover'), {
        method: 'POST',
        body,
      }),
  });
}
